import { api } from './api';

export interface SalesReportFilters {
  startDate?: string;
  endDate?: string;
  locationId?: string;
  userId?: string;
  page?: number;
  limit?: number;
}

export interface SalesSummaryFilters {
  startDate?: string;
  endDate?: string;
  locationId?: string;
  userId?: string;
}

export interface InventoryReportFilters {
  locationId?: string;
  lowStockOnly?: boolean;
  page?: number;
  limit?: number;
}

export interface TopProductsFilters {
  startDate?: string;
  endDate?: string;
  locationId?: string;
  limit?: number;
}

export interface SalesReportItem {
  id: string;
  total: number;
  subtotal: number;
  tax: number;
  discount: number;
  paymentMethod: string;
  createdAt: string;
  user: {
    id: string;
    name: string;
    role: string;
  };
  location: {
    id: string;
    name: string;
  };
  saleItems: Array<{
    id: string;
    quantity: number;
    unitPrice: number;
    total: number;
    product: {
      name: string;
      sku: string;
    };
  }>;
}

export interface SalesReportResponse {
  success: boolean;
  data: SalesReportItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SalesSummary {
  totalSales: number;
  averageTicket: number;
  transactionCount: number;
  salesByLocation: Array<{
    locationId: string;
    _sum: { total: number };
    _count: number;
    location: {
      id: string;
      name: string;
    };
  }>;
  salesByPaymentMethod: Array<{
    paymentMethod: string;
    _sum: { total: number };
    _count: number;
  }>;
  salesByUser: Array<{
    userId: string;
    _sum: { total: number };
    _count: number;
    user: {
      id: string;
      name: string;
      role: string;
    };
  }>;
}

export interface SalesSummaryResponse {
  success: boolean;
  data: SalesSummary;
}

export interface InventoryItem {
  id: string;
  quantity: number;
  value: number;
  status: 'NORMAL' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'NEGATIVE';
  product: {
    id: string;
    name: string;
    sku: string;
    category: string;
    costPrice: number;
    salePrice: number;
    minStock: number;
    unit: string;
  };
  location: {
    id: string;
    name: string;
  };
}

export interface InventoryReport {
  items: InventoryItem[];
  summary: {
    totalItems: number;
    totalValue: number;
    lowStockCount: number;
    outOfStockCount: number;
    negativeStockCount: number;
  };
}

export interface InventoryReportResponse {
  success: boolean;
  data: InventoryReport;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TopProduct {
  rank: number;
  product: {
    id: string;
    name: string;
    sku: string;
    category: string;
    salePrice: number;
  };
  quantitySold: number;
  revenue: number;
  percentage: number;
  salesCount: number;
}

export interface TopProductsResponse {
  success: boolean;
  data: TopProduct[];
}

export const reportsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSalesReport: builder.query<SalesReportResponse, SalesReportFilters>({
      query: (filters = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value.toString());
          }
        });
        return {
          url: `/reports/sales?${searchParams.toString()}&_t=${Date.now()}`,
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        };
      },
      providesTags: ['Report', 'Sale'],
      keepUnusedDataFor: 0, // No cache - always fetch fresh data
    }),

    getReportsSalesSummary: builder.query<SalesSummaryResponse, SalesSummaryFilters>({
      query: (filters = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value.toString());
          }
        });
        return {
          url: `/reports/sales-summary?${searchParams.toString()}&_t=${Date.now()}`,
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        };
      },
      providesTags: ['Report', 'Sale'],
      keepUnusedDataFor: 0, // No cache - always fetch fresh data
    }),

    getInventoryReport: builder.query<InventoryReportResponse, InventoryReportFilters>({
      query: (filters = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value.toString());
          }
        });
        return {
          url: `/reports/inventory?${searchParams.toString()}&_t=${Date.now()}`,
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache', 
            'Expires': '0'
          }
        };
      },
      providesTags: ['Report', 'Inventory'],
      keepUnusedDataFor: 0, // No cache - always fetch fresh data
    }),

    getTopProducts: builder.query<TopProductsResponse, TopProductsFilters>({
      query: (filters = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value.toString());
          }
        });
        return {
          url: `/reports/top-products?${searchParams.toString()}&_t=${Date.now()}`,
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        };
      },
      providesTags: ['Report', 'Sale'],
      keepUnusedDataFor: 0, // No cache - always fetch fresh data
    }),
  }),
});

export const {
  useGetSalesReportQuery,
  useGetReportsSalesSummaryQuery,
  useGetInventoryReportQuery,
  useGetTopProductsQuery,
} = reportsApi;